import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import BranchSelector from './BranchSelector';
import type { RepoBranchConfig } from '@/hooks';

type Props = {
  configs: RepoBranchConfig[];
  selectedRepoIds: Set<string>;
  onRepoToggle: (repoId: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onBranchChange: (repoId: string, branch: string) => void;
  isLoading?: boolean;
  showLabel?: boolean;
  className?: string;
};

export function RepoBranchSelector({
  configs,
  selectedRepoIds,
  onRepoToggle,
  onSelectAll,
  onSelectNone,
  onBranchChange,
  isLoading,
  showLabel = true,
  className,
}: Props) {
  const { t } = useTranslation('tasks');

  if (configs.length === 0) {
    return null;
  }

  // Single repo: no need for selection UI
  if (configs.length === 1) {
    const config = configs[0];
    return (
      <div className={className}>
        {showLabel && (
          <Label className="text-sm font-medium">
            {t('repoBranchSelector.label')}{' '}
            <span className="text-destructive">*</span>
          </Label>
        )}
        <BranchSelector
          branches={config.branches}
          selectedBranch={config.targetBranch}
          onBranchSelect={(branch) => onBranchChange(config.repoId, branch)}
          placeholder={
            isLoading
              ? t('createAttemptDialog.loadingBranches')
              : t('createAttemptDialog.selectBranch')
          }
        />
      </div>
    );
  }

  // Multiple repos: show selection UI
  const selectedCount = selectedRepoIds.size;
  const allSelected = selectedCount === configs.length;
  const noneSelected = selectedCount === 0;

  return (
    <div className={className}>
      {showLabel && (
        <div className="flex items-center justify-between mb-3">
          <Label className="text-sm font-medium">
            {t('repoBranchSelector.selectRepos')}
          </Label>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelectAll}
              disabled={allSelected}
              className="h-7 px-2 text-xs"
            >
              {t('repoBranchSelector.selectAll')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelectNone}
              disabled={noneSelected}
              className="h-7 px-2 text-xs"
            >
              {t('repoBranchSelector.selectNone')}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {configs.map((config) => (
          <div
            key={config.repoId}
            className={`flex items-center gap-3 p-2 rounded border ${
              config.isSelected
                ? 'bg-background'
                : 'bg-muted/50 opacity-60'
            }`}
          >
            <Checkbox
              id={`repo-${config.repoId}`}
              checked={config.isSelected}
              onCheckedChange={() => onRepoToggle(config.repoId)}
            />
            <div className="flex-1 min-w-0">
              <Label
                htmlFor={`repo-${config.repoId}`}
                className="text-sm font-medium cursor-pointer"
              >
                {config.repoDisplayName}
              </Label>
            </div>
            <div className="flex-shrink-0 w-40">
              <BranchSelector
                branches={config.branches}
                selectedBranch={config.targetBranch}
                onBranchSelect={(branch) =>
                  onBranchChange(config.repoId, branch)
                }
                placeholder={
                  isLoading
                    ? t('createAttemptDialog.loadingBranches')
                    : t('createAttemptDialog.selectBranch')
                }
                disabled={!config.isSelected}
              />
            </div>
          </div>
        ))}
      </div>

      {noneSelected && (
        <p className="text-sm text-destructive mt-2">
          {t('repoBranchSelector.selectAtLeastOne')}
        </p>
      )}
    </div>
  );
}

export default RepoBranchSelector;